import { EMPTY } from 'rxjs';

import { QtFilterDirective } from '../../directives';
import { transformerFactory } from '../transform';
import {
  ArrayFilter,
  arrayFilter,
  ArrayFilterChange,
  ArrayFilterValues,
} from './array-processing';
import {
  CheckboxFilter,
  checkboxFilter,
  CheckboxFilterChange,
  CheckboxValues,
} from './checkbox';
import { DateFilter, dateFilter, DateFilterChange, DateValues } from './date';
import {
  NumericFilter,
  numericFilter,
  NumericFilterChange,
  NumericValues,
} from './numeric';
import {
  SelectFilter,
  selectFilter,
  SelectFilterChange,
  SelectValues,
} from './select';

export type FilterValues =
  | NumericValues
  | DateValues
  | CheckboxValues
  | SelectValues
  | ArrayFilterValues;

/** Filter configuration that can be passed to component. */
export type Filter =
  | NumericFilter
  | DateFilter
  | CheckboxFilter
  | SelectFilter
  | ArrayFilter;

/** Output that filter component will produce. */
export interface FilterChange {
  numeric: NumericFilterChange[];
  date: DateFilterChange[];
  checkbox: CheckboxFilterChange[];
  select: SelectFilterChange[];
  array: ArrayFilterChange[];
}

/**
 * Returns filtered data for given filter configuration.
 *
 * @param data Data to filter.
 * @param filter Instance of {@link QtFilterDirective}.
 * @param valueAccessor
 */
export function filterData<T>(
  data: T[],
  filter: QtFilterDirective,
  valueAccessor: (row: T, id: string, subId?: string, type?: string) => any
): T[] {
  if (!filter || !filter.activeFilters) {
    return data;
  }

  const { numeric, date, checkbox, select, array } = filter.activeFilters;

  return data.filter(row => {
    /** Only run predicate function if there is active filter. */
    const numericResult = numeric
      ? numericFilter(numeric, row, valueAccessor)
      : true;
    const dateResult = date ? dateFilter(date, row, valueAccessor) : true;
    const checkboxResult = checkbox
      ? checkboxFilter(checkbox, row, valueAccessor)
      : true;
    const selectResult = select
      ? selectFilter(select, row, valueAccessor)
      : true;
    const arrayResult = array ? arrayFilter(array, row, valueAccessor) : true;

    return (
      numericResult &&
      dateResult &&
      checkboxResult &&
      selectResult &&
      arrayResult
    );
  });
}

/**
 * Filter data stream by {@link QtFilterDirective} configuration.
 *
 * @param filter Instance of {@link QtFilterDirective} to get events and config from.
 * @param valueAccessor
 */
export function filter<T>(
  filter: QtFilterDirective,
  valueAccessor: (row: T, id: string, subId?: string, type?: string) => any
) {
  return transformerFactory<T, FilterChange>(
    filter ? filter.filterChange : EMPTY,
    ([data]) => filterData(data, filter, valueAccessor)
  );
}
